import { useIntl } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { CrossFlowFrontendProvider } from "@/components/cross-flow/use-cross-flow-frontend";
import BannerAlert, { BannerProps } from "@/components/wpl/banner-alert/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import GlobalFooter from "@/patterns/global-footer/src/index";

import { JiraSoftwarePageProps } from "../../../../pages/jira-software-page/jira-software-page";
import { ParentScrollTracking } from "../../../../utils/scroll-tracking";
import {
    BlogsSection,
    CTASection,
    DevOpsSection,
    LoggedInHeroSection,
    LoggedOutHeroSection,
    IntegrationSection,
    ScalingSection,
    TabsSection,
    WorkflowSection
} from "../sections/productionized/logged-in-jira";
import { filterAvailableJiraSites } from "../sections/productionized/logged-in-jira/logged-in-hero/logged-in-hero";
import JiraSoftwareNavV2 from "../sections/productionized/software-jira-navbar";
import { ToastSection } from "../sections/toast-section";

export const LoggedInJiraProductTourOverview = ({
    availableProducts,
    userProfile
}: JiraSoftwarePageProps) => {
    const intl = useIntl();
    const availableJiraSites = filterAvailableJiraSites(availableProducts);

    // Not an experiment, but using experiment logic to turn on banner for TEAM 24 5/1 10:30AM PST english only traffic until localized
    const showSporkBannerAnnoucement = FeatureGates.checkGate(
        "jfind-spork-announcement"
    );

    const sporkBannerMessages: BannerProps = {
        identifier: "jira-software.banner-alert.spork-annoucement",
        text: intl.formatMessage({
            id: "jira-software.banner-alert-spork.text",
            defaultMessage:
                "Jira Software and Jira Work Management are now Jira - every team's tool for project management."
        }),
        linkText: intl.formatMessage({
            id: "jira-software.banner-alert-spork.link-text",
            defaultMessage: "See what’s new"
        }),
        linkUrl: useLocalizedHref("/blog/announcements/the-next-era-of-jira"),
        dataEventProps: {
            "data-product-key": "jiraSoftware",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Learn more",
            "data-label-english": "See what’s new"
        }
    };

    const aiBannerMessages: BannerProps = {
        identifier: "jira-software.banner-alert.atlassian-intelligence",
        text: intl.formatMessage({
            id: "jira-software.banner-alert.text",
            defaultMessage:
                "Transform teamwork with Atlassian Intelligence - now available."
        }),
        linkText: intl.formatMessage({
            id: "jira-software.banner-alert.link-text",
            defaultMessage: "Learn more"
        }),
        linkUrl: useLocalizedHref("/software/artificial-intelligence"),
        dataEventProps: {
            "data-product-key": "jiraSoftware",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Learn more",
            "data-label-english": "Learn more"
        }
    };

    // ATLAS-53087: Show welcome message and product link to logged in users with available Jira sites
    const HeroSection =
        userProfile && availableJiraSites.length > 0 ? (
            <LoggedInHeroSection
                availableJiraSites={availableJiraSites}
                userProfileName={userProfile?.name}
            />
        ) : (
            <LoggedOutHeroSection />
        );

    return (
        <CrossFlowFrontendProvider
            product={ProductKeys.JIRA_SOFTWARE}
            pageName={"wacJswProductTourPage"}
        >
            <div>
                <JiraSoftwareNavV2 />
                <ParentScrollTracking scrollTrackingEnabled={true} pageId="jsw">
                    {showSporkBannerAnnoucement ? (
                        <BannerAlert {...sporkBannerMessages} />
                    ) : (
                        <BannerAlert {...aiBannerMessages} />
                    )}
                    {HeroSection}
                    <TabsSection />
                    <WorkflowSection />
                    <IntegrationSection />
                    <DevOpsSection />
                    <BlogsSection />
                    <ScalingSection />
                    <CTASection />
                    <ToastSection />
                    <GlobalFooter />
                </ParentScrollTracking>
            </div>
        </CrossFlowFrontendProvider>
    );
};
